import { useCallback, useRef, useState } from 'react';

interface History<T> {
  past: T[];
  present: T;
  future: T[];
}

export interface HistoryState<T> {
  value: T;
  /** Replaces the value (or derives it from the current one) and records the step for undo. */
  set: (next: T | ((current: T) => T)) => void;
  /** Replaces the value and forgets all history (after loading the latest from the server). */
  reset: (next: T) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * State with undo and redo. Changes that follow each other within `mergeMs` (typing, dragging a slider)
 * count as one step, and at most `limit` steps are kept.
 */
export function useHistoryState<T>(init: () => T, { limit = 100, mergeMs = 800 }: { limit?: number; mergeMs?: number } = {}): HistoryState<T> {
  const [h, setH] = useState<History<T>>(() => ({ past: [], present: init(), future: [] }));
  const lastAt = useRef(0);

  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      const now = Date.now();
      const merge = now - lastAt.current < mergeMs;
      lastAt.current = now;
      setH((s) => {
        const value = typeof next === 'function' ? (next as (c: T) => T)(s.present) : next;
        if (Object.is(value, s.present)) return s;
        const past = merge && s.past.length > 0 ? s.past : [...s.past, s.present].slice(-limit);
        return { past, present: value, future: [] };
      });
    },
    [limit, mergeMs],
  );
  const reset = useCallback((next: T) => {
    lastAt.current = 0;
    setH({ past: [], present: next, future: [] });
  }, []);
  const undo = useCallback(() => {
    lastAt.current = 0;
    setH((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1]!, future: [s.present, ...s.future] } : s));
  }, []);
  const redo = useCallback(() => {
    lastAt.current = 0;
    setH((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0]!, future: s.future.slice(1) } : s));
  }, []);

  return { value: h.present, set, reset, undo, redo, canUndo: h.past.length > 0, canRedo: h.future.length > 0 };
}
